/**
 * useJournal -- Trade journal entries, tags, analytics, and search.
 */
import { create } from 'zustand'
import {
  getJournalEntries,
  createJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
  searchJournalEntries,
  getJournalTags,
  getJournalAnalytics,
} from '@/lib/api'
import type {
  JournalEntry,
  JournalEntryCreate,
  JournalTagCount,
  JournalAnalytics,
} from '@/lib/types'

interface JournalState {
  entries: JournalEntry[]
  tags: JournalTagCount[]
  analytics: JournalAnalytics | null
  loading: boolean
  loadingAnalytics: boolean

  fetchEntries: () => Promise<void>
  fetchTags: () => Promise<void>
  fetchAnalytics: () => Promise<void>
  search: (q: string) => Promise<void>
  create: (body: JournalEntryCreate) => Promise<void>
  update: (id: number, body: Partial<JournalEntryCreate>) => Promise<void>
  remove: (id: number) => Promise<void>
}

export const useJournal = create<JournalState>()((set) => ({
  entries: [],
  tags: [],
  analytics: null,
  loading: false,
  loadingAnalytics: false,

  fetchEntries: async () => {
    set({ loading: true })
    try {
      const entries = await getJournalEntries()
      set({ entries, loading: false })
    } catch { set({ loading: false }) }
  },

  fetchTags: async () => {
    try {
      const tags = await getJournalTags()
      set({ tags })
    } catch { /* ignore */ }
  },

  fetchAnalytics: async () => {
    set({ loadingAnalytics: true })
    try {
      const analytics = await getJournalAnalytics()
      set({ analytics, loadingAnalytics: false })
    } catch { set({ loadingAnalytics: false }) }
  },

  search: async (q: string) => {
    set({ loading: true })
    try {
      const entries = await searchJournalEntries(q)
      set({ entries, loading: false })
    } catch { set({ loading: false }) }
  },

  create: async (body: JournalEntryCreate) => {
    try {
      await createJournalEntry(body)
      const entries = await getJournalEntries()
      const tags = await getJournalTags()
      set({ entries, tags })
    } catch { /* ignore */ }
  },

  update: async (id: number, body: Partial<JournalEntryCreate>) => {
    try {
      await updateJournalEntry(id, body)
      const entries = await getJournalEntries()
      set({ entries })
    } catch { /* ignore */ }
  },

  remove: async (id: number) => {
    try {
      await deleteJournalEntry(id)
      const entries = await getJournalEntries()
      const tags = await getJournalTags()
      set({ entries, tags })
    } catch { /* ignore */ }
  },
}))
